/**
 * Invoice Template — mirrors the Indian Quotation Template structure.
 * Returns a raw HTML string ready for dangerouslySetInnerHTML or print/PDF.
 */
export const getInvoiceTemplate = (data, companyInfo = {}, customTermsHtml = "") => {
  const {
    invoiceNo = "",
    date = "",
    customer = "",
    customerPhone = "",

    // Billing
    billingAddressLine1 = "",
    billingAddressLine2 = "",
    billingCity = "",
    billingState = "",
    billingPincode = "",

    // Shipping / Delivery
    deliveryContactPerson = "",
    deliveryPhone = "",
    deliveryAddressLine1 = "",
    deliveryAddressLine2 = "",
    deliveryCity = "",
    deliveryState = "",
    deliveryPincode = "",

    // Items
    items = [],

    // Financials
    taxableAmount = 0,
    totalDiscount = 0,
    totalTax = 0,
    netBill = 0,
    adjustment = 0,
    netPayable = 0,
    charges = [],

    // Extras
    remarks = "",
    store = null,
    driverName = "",
    truckNumber = "",
    dispatchNo = "",
    dispatchStartedAt = "",
    dispatchNotes = "",
  } = data;

  const {
    companyName = "Generic",
    companyAddress = "",
    companyEmail = "",
    companyPhone = "",
    companyGst = "",
    logoUrl = "",
    digitalSignatureUrl = "",
  } = companyInfo;


  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-GB");
  };

  const fmtINR = (val) => {
    const n = parseFloat(val);
    if (isNaN(n)) return "—";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  };

  // logoUrl is already a fully-resolved URL from useCompanyInfo

  const hasShipping =
    deliveryAddressLine1 || deliveryCity || deliveryContactPerson;

  // ── Items rows ─────────────────────────────────────────────────────────────
  const itemRows = (items || [])
    .map((it, idx) => {
      const itemObj =
        it.item && typeof it.item === "object" ? it.item : {};
      const name =
        itemObj.itemName ||
        itemObj.name ||
        itemObj.description ||
        it.itemName ||
        "Item";
      const code = itemObj.itemCode || itemObj.articleId || "";
      const qty = it.qty || 0;
      const unit = it.unit || "";
      const rate = parseFloat(it.rate || 0);
      const disc = parseFloat(it.discountPercent || 0);
      const tax = parseFloat(it.taxPercent || 0);
      const amount = parseFloat(it.amount || rate * qty);

      return `
      <tr>
        <td style="text-align:center; vertical-align:middle; font-size:8px; padding:3px 6px;">${idx + 1}</td>
        <td style="vertical-align:top; padding:3px 8px;">
          <div style="font-weight:600; font-size:8.5px;">${name}</div>
          ${code ? `<div style="font-size:7.5px; color:#555;">${code}</div>` : ""}
        </td>
        <td style="text-align:center; vertical-align:middle; font-size:8px; padding:3px 6px;">${qty} ${unit}</td>
        <td style="text-align:right; vertical-align:middle; font-size:8px; padding:3px 8px;">${fmtINR(rate)}</td>
        <td style="text-align:center; vertical-align:middle; font-size:8px; padding:3px 6px;">${disc > 0 ? disc + "%" : "—"}</td>
        <td style="text-align:center; vertical-align:middle; font-size:8px; padding:3px 6px;">${tax > 0 ? tax + "%" : "—"}</td>
        <td style="text-align:right; vertical-align:middle; font-weight:700; font-size:8.5px; padding:3px 8px;">${fmtINR(amount)}</td>
      </tr>`;
    })
    .join("");

  // ── Charges rows ───────────────────────────────────────────────────────────
  const chargeRows = (charges || [])
    .filter((c) => c.amount)
    .map(
      (c) => `
    <tr>
      <td colspan="5" style="text-align:right; font-size:8px; padding:2px 8px; border:none;">${c.name || "Charge"}</td>
      <td style="text-align:right; font-size:8px; padding:2px 8px; border:none;">${fmtINR(c.amount)}</td>
    </tr>`
    )
    .join("");

  // ── Template ───────────────────────────────────────────────────────────────
  return `
  <div id="invoice-root" style="width:715px; background:#fff; border:0.5pt solid #9ca3af; box-sizing:border-box; font-family:'Inter',sans-serif; font-size:9px; color:#000; margin:0 auto; position:relative;">
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet">
    <style>
      #invoice-root { -webkit-print-color-adjust:exact; background:#fff !important; color:#000 !important; letter-spacing:-0.1px; }
      #invoice-root * { box-sizing:border-box; }
      .lc  { width:140px; font-weight:400; padding:2px 8px !important; font-size:8.5px !important; color:#000; border:none !important; }
      .col { width:10px;  font-weight:400; padding:2px 0  !important; text-align:center; color:#000; border:none !important; font-size:8.5px !important; }
      .vc  { font-weight:600; padding:2px 8px !important; font-size:8.5px !important; color:#000; border:none !important; }
      .ht  { width:100%; border-collapse:collapse; border:none; }
      .ht td { border:none !important; padding:0 !important; }
      .st, .it { width:100%; border-collapse:collapse; margin:0; }
      .st > tbody > tr > td,
      .it > thead > tr > th,
      .it > tbody > tr > td { border:0.5pt solid #9ca3af !important; padding:2px 8px; color:#000 !important; background:#fff !important; font-size:8px !important; }
      .st td:first-child, .it th:first-child, .it td:first-child { border-left:none !important; }
      .st td:last-child,  .it th:last-child,  .it td:last-child  { border-right:none !important; }
      .st tr:first-child td, .it thead tr th { border-top:none !important; }
      .it th { font-weight:700; text-align:center; background:#f8fafc !important; border-bottom:0.5pt solid #9ca3af !important; }
      .fb { font-weight:700; }
      .tc { text-align:center; }
      .tr { text-align:right; }
    </style>

    <!-- ① HEADER -->
    <div style="padding:6px 15px; border-bottom:0.5pt solid #9ca3af;">
      <table class="ht">
        <tr>
          <td style="width:200px;">
            ${logoUrl ? `<img src="${logoUrl}" style="width:155px; height:50px; object-fit:contain;" />` : `<div style="font-size:16px; font-weight:700;">${companyName}</div>`}
          </td>
          <td style="text-align:right; vertical-align:middle;">
            <div style="font-size:16px; font-weight:700; letter-spacing:-0.2px;">${companyName}</div>
            <div style="margin-top:2px; line-height:1.5; font-size:10px; color:#000;">
              ${companyAddress ? companyAddress.replace(/\n/g, "<br/>") : ""}
              ${companyGst ? `<br/>GSTIN ${companyGst}` : ""}
              ${companyEmail ? `<br/>${companyEmail}` : ""}
              ${companyPhone ? ` | ${companyPhone}` : ""}
            </div>
          </td>
        </tr>
      </table>
    </div>

    <!-- ② TITLE -->
    <div class="tc fb" style="padding:3px 0; font-size:12px; border-bottom:0.5pt solid #9ca3af; letter-spacing:1.5px;">
      TAX INVOICE
    </div>

    <!-- ③ META DETAILS -->
    <table class="st" style="border-bottom:0.5pt solid #9ca3af;">
      <tr>
        <td style="width:50%; padding:0 !important;">
          <table style="width:100%; border-collapse:collapse; margin-bottom:6px;">
            <tr><td class="lc">Invoice No.</td><td class="col">:</td><td class="vc">${invoiceNo}</td></tr>
            <tr><td class="lc">Invoice Date</td><td class="col">:</td><td class="vc">${formatDate(date)}</td></tr>
            ${store?.storeName ? `<tr><td class="lc">Store</td><td class="col">:</td><td class="vc">${store.storeName}</td></tr>` : ""}
          </table>
        </td>
        <td style="width:50%; padding:0 !important;">
          <table style="width:100%; border-collapse:collapse; margin-bottom:6px;">
            <tr><td class="lc">Customer</td><td class="col">:</td><td class="vc">${customer}</td></tr>
            ${customerPhone ? `<tr><td class="lc">Phone</td><td class="col">:</td><td class="vc">${customerPhone}</td></tr>` : ""}
          </table>
        </td>
      </tr>
    </table>

    <!-- ④ BILLING & SHIPPING -->
    <table class="st" style="border-bottom:0.5pt solid #9ca3af;">
      <tr>
        <td style="width:50%; padding:0 !important;">
          <div style="padding:2px 8px; font-weight:700; font-style:italic; font-size:9px;">Billed To :</div>
          <table style="width:100%; border-collapse:collapse; margin-bottom:6px;">
            <tr><td class="lc">Name</td><td class="col">:</td><td class="vc">${customer}</td></tr>
            ${billingAddressLine1 ? `<tr><td class="lc">Address</td><td class="col">:</td><td class="vc">${billingAddressLine1}${billingAddressLine2 ? ", " + billingAddressLine2 : ""}</td></tr>` : ""}
            ${billingCity ? `<tr><td class="lc">City</td><td class="col">:</td><td class="vc">${billingCity}</td></tr>` : ""}
            ${billingState ? `<tr><td class="lc">State</td><td class="col">:</td><td class="vc">${billingState}</td></tr>` : ""}
            ${billingPincode ? `<tr><td class="lc">Pincode</td><td class="col">:</td><td class="vc">${billingPincode}</td></tr>` : ""}
            ${customerPhone ? `<tr><td class="lc">Mobile</td><td class="col">:</td><td class="vc">${customerPhone}</td></tr>` : ""}
          </table>
        </td>
        <td style="width:50%; padding:0 !important; border-left:0.5pt solid #9ca3af;">
          <div style="padding:2px 8px; font-weight:700; font-style:italic; font-size:9px;">Shipped To :</div>
          <table style="width:100%; border-collapse:collapse; margin-bottom:6px;">
            <tr><td class="lc">Name</td><td class="col">:</td><td class="vc">${deliveryContactPerson || customer}</td></tr>
            ${deliveryAddressLine1 ? `<tr><td class="lc">Address</td><td class="col">:</td><td class="vc">${deliveryAddressLine1}${deliveryAddressLine2 ? ", " + deliveryAddressLine2 : ""}</td></tr>` : ""}
            ${deliveryCity ? `<tr><td class="lc">City</td><td class="col">:</td><td class="vc">${deliveryCity}</td></tr>` : ""}
            ${deliveryState ? `<tr><td class="lc">State</td><td class="col">:</td><td class="vc">${deliveryState}</td></tr>` : ""}
            ${deliveryPincode ? `<tr><td class="lc">Pincode</td><td class="col">:</td><td class="vc">${deliveryPincode}</td></tr>` : ""}
            ${deliveryPhone ? `<tr><td class="lc">Mobile</td><td class="col">:</td><td class="vc">${deliveryPhone}</td></tr>` : ""}
          </table>
        </td>
      </tr>
    </table>

    <!-- ⑤ ITEMS TABLE -->
    <table class="it" style="table-layout:fixed; width:100%;">
      <thead>
        <tr>
          <th style="width:30px; text-align:left !important; font-size:8.5px !important; padding:3px 6px;">S.No.</th>
          <th style="text-align:left !important; font-size:8.5px !important; padding:3px 8px;">Item Description</th>
          <th style="width:70px; font-size:8.5px !important; padding:3px 6px;">Qty</th>
          <th style="width:90px; font-size:8.5px !important; padding:3px 8px;">Rate</th>
          <th style="width:50px; font-size:8.5px !important; padding:3px 6px;">Disc.</th>
          <th style="width:50px; font-size:8.5px !important; padding:3px 6px;">Tax</th>
          <th style="width:100px; text-align:right !important; font-size:8.5px !important; padding:3px 8px;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows || `<tr><td colspan="7" style="text-align:center; padding:10px; font-size:8px; color:#888;">No items</td></tr>`}
      </tbody>
    </table>

    <!-- ⑥ TOTALS -->
    <table class="st" style="border-top:0.5pt solid #9ca3af;">
      <tr>
        <td style="width:60%; border:none !important; vertical-align:top; padding:8px !important;">
          ${remarks ? `<div style="font-size:8px; line-height:1.5;"><span style="font-weight:700;">Remarks:</span> ${remarks}</div>` : ""}
        </td>
        <td style="width:40%; padding:0 !important;">
          <table style="width:100%; border-collapse:collapse;">
            <tr>
              <td style="padding:2px 8px; font-size:8px;">Subtotal (Taxable)</td>
              <td style="padding:2px 8px; text-align:right; font-size:8px;">${fmtINR(taxableAmount)}</td>
            </tr>
            ${parseFloat(totalDiscount) > 0 ? `
            <tr>
              <td style="padding:2px 8px; font-size:8px; color:#16a34a;">Total Discount</td>
              <td style="padding:2px 8px; text-align:right; font-size:8px; color:#16a34a;">- ${fmtINR(totalDiscount)}</td>
            </tr>` : ""}
            <tr>
              <td style="padding:2px 8px; font-size:8px;">Total Tax (GST)</td>
              <td style="padding:2px 8px; text-align:right; font-size:8px;">${fmtINR(totalTax)}</td>
            </tr>
            ${chargeRows}
            ${parseFloat(adjustment) !== 0 ? `
            <tr>
              <td style="padding:2px 8px; font-size:8px;">Adjustment</td>
              <td style="padding:2px 8px; text-align:right; font-size:8px;">${fmtINR(adjustment)}</td>
            </tr>` : ""}
            <tr style="background:#e8f5e9;">
              <td style="padding:4px 8px; font-weight:700; font-size:9.5px; border-top:0.5pt solid #9ca3af !important;">Total Net Payable</td>
              <td style="padding:4px 8px; text-align:right; font-weight:700; font-size:9.5px; border-top:0.5pt solid #9ca3af !important;">${fmtINR(netPayable)}</td>
            </tr>
          </table>
        </td>
      </tr>
    </table>

    <!-- ⑦ DISPATCH DETAILS -->
    ${(truckNumber || driverName || dispatchNo) ? `
    <table class="st" style="border-top:0.5pt solid #9ca3af; border-bottom:none;">
      <tr>
        <td style="padding:6px 8px !important;">
          <div style="font-weight:700; font-size:9px; border-bottom:0.5pt solid #9ca3af; padding-bottom:2px; margin-bottom:4px;">Dispatch Details</div>
          <table style="width:100%; border-collapse:collapse; margin-bottom:2px;">
            <tr>
              <td style="width:50%; vertical-align:top; padding:0;">
                <table style="width:100%;">
                  ${dispatchNo ? `<tr><td class="lc">Dispatch No</td><td class="col">:</td><td class="vc">${dispatchNo}</td></tr>` : ''}
                  ${dispatchStartedAt ? `<tr><td class="lc">Dispatch Date</td><td class="col">:</td><td class="vc">${formatDate(dispatchStartedAt)}</td></tr>` : ''}
                </table>
              </td>
              <td style="width:50%; vertical-align:top; padding:0;">
                <table style="width:100%;">
                  ${truckNumber ? `<tr><td class="lc">Truck No</td><td class="col">:</td><td class="vc">${truckNumber}</td></tr>` : ''}
                  ${driverName ? `<tr><td class="lc">Driver</td><td class="col">:</td><td class="vc">${driverName}</td></tr>` : ''}
                </table>
              </td>
            </tr>
          </table>
          ${dispatchNotes ? `<div style="font-size:8px; line-height:1.5; margin-top:2px;"><span style="font-weight:700;">Notes:</span> ${dispatchNotes}</div>` : ""}
        </td>
      </tr>
    </table>
    ` : ""}

    <!-- ⑧ FOOTER — Terms & Signature -->
    <table class="st" style="table-layout:fixed; width:100%; border-top:0.5pt solid #9ca3af;">
      <tr>
        <td style="width:60%; padding:6px 8px !important; vertical-align:top; border-right:0.5pt solid #9ca3af;">
          <div style="font-weight:700; font-size:8.5px; border-bottom:0.5pt solid #9ca3af; padding-bottom:2px; margin-bottom:4px;">Terms &amp; Conditions</div>
          <div style="font-size:8px; line-height:1.4; color:#333;">
            ${customTermsHtml || `
            <div>1. Payment is due upon receipt of invoice.</div>
            <div>2. Goods once sold will not be taken back.</div>
            <div>3. All disputes subject to local jurisdiction only.</div>
            <div>4. E &amp; O.E.</div>
            `}
          </div>
        </td>
        <td style="width:40%; padding:6px 8px !important; vertical-align:top; text-align:center;">
          ${digitalSignatureUrl ? 
            `<div style="margin-top:5px; margin-bottom:5px; display:flex; justify-content:center; align-items:center;"><img src="${digitalSignatureUrl}" style="height:35px; object-fit:contain;" /></div>` : 
            `<div style="margin-top:45px;"></div>`
          }
          <div style="border-top:0.5pt solid #9ca3af; padding-top:4px; font-size:8px; font-weight:600; text-align:center;">
            Authorized Signatory
          </div>
        </td>
      </tr>
    </table>
  </div>`;
};
